/**
 * Unified Database Operations Layer
 * Single source of truth for all database interactions
 * Implements Swiss legal entity management with multi-tenant support
 *
 * Database: Neon PostgreSQL (syncup-brand-legal)
 * Schema: legalsyncup
 */

import { sql, query, transaction as neonTransaction, LEGALSYNCUP_SCHEMA, isDatabaseAvailable } from '@/integrations/neon';
import type {
  SwissEntity,
  SwissEntityInsert,
  SwissEntityUpdate,
  ContentDocument,
  ContentDocumentInsert,
  Brand,
  BrandInsert,
} from '@/integrations/neon';
import { envLog, envError } from '@/config/environment';
import {
  withEntityPerformanceSpan,
  withApprovalPerformanceSpan,
  withDatabasePerformanceSpan,
  captureEntityException,
  captureApprovalWorkflowException
} from '@/lib/sentry';

// Check if database is available (server-side only)
const dbAvailable = isDatabaseAvailable();

// Helper to check database availability
const requireDb = <T>(): DatabaseResult<T> | null => {
  if (!dbAvailable || !sql) {
    return { data: null, error: { code: 'NO_DB', message: 'Database not available. Use API endpoints.' } };
  }
  return null;
};

/**
 * Get a non-null sql reference.
 * Only call after requireDb() has confirmed availability.
 */
const getSql = () => {
  // After requireDb() passes, sql is guaranteed non-null
  return sql!;
};

// Base database error type
export interface DatabaseError {
  code: string;
  message: string;
  details?: unknown;
}

// Database operation result type
export type DatabaseResult<T> = {
  data: T | null;
  error: DatabaseError | null;
  count?: number;
};

// Database operation options
export interface DatabaseOptions {
  tenantId?: string;
  userId?: string;
  skipAuditLog?: boolean;
  useCache?: boolean;
}

/**
 * Entity Operations Class
 * Handles Swiss legal entity CRUD operations
 */
class EntityOperations {
  async createEntity(entity: SwissEntityInsert, options: DatabaseOptions = {}): Promise<DatabaseResult<SwissEntity>> {
    const dbCheck = requireDb<SwissEntity>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_create', entity.legal_form || 'unknown', async () => {
      try {
        envLog('Creating entity', { entity: entity.name, tenantId: options.tenantId });

        const tenantId = options.tenantId || entity.tenant_id || undefined;

        const result = await withDatabasePerformanceSpan('query', 'swiss_entities', async () => {
          const db = getSql();
          return await db`
            INSERT INTO legalsyncup.swiss_entities
            (name, uid, legal_form, purpose, capital, registration_date, canton, municipality, status, tenant_id)
            VALUES (
              ${entity.name},
              ${entity.uid},
              ${entity.legal_form},
              ${entity.purpose || null},
              ${entity.capital || null},
              ${entity.registration_date || null},
              ${entity.canton || null},
              ${entity.municipality || null},
              ${entity.status || 'active'},
              ${tenantId}
            )
            RETURNING *
          `;
        });

        const data = result[0] as SwissEntity;

        // Log audit trail if not skipped
        if (!options.skipAuditLog && data) {
          await this.logAuditEvent('entity_created', data.id, options.userId);
        }

        return { data, error: null };
      } catch (err) {
        envError('Entity creation exception', err);
        captureEntityException(err as Error, {
          operation: 'create',
          legal_form: entity.legal_form,
          canton: entity.canton ?? undefined,
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'CREATE_ERROR', message: 'Failed to create entity', details: err }
        };
      }
    });
  }

  async getEntity(id: string, options: DatabaseOptions = {}): Promise<DatabaseResult<SwissEntity>> {
    const dbCheck = requireDb<SwissEntity>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_validate', 'get_by_id', async () => {
      try {
        envLog('Fetching entity', { id, tenantId: options.tenantId });

        const result = await withDatabasePerformanceSpan('query', 'swiss_entities', async () => {
          const db = getSql();
          if (options.tenantId) {
            return await db`
              SELECT * FROM legalsyncup.swiss_entities
              WHERE id = ${id} AND tenant_id = ${options.tenantId}
            `;
          } else {
            return await db`
              SELECT * FROM legalsyncup.swiss_entities
              WHERE id = ${id}
            `;
          }
        });

        if (result.length === 0) {
          return { data: null, error: { code: 'NOT_FOUND', message: 'Entity not found' } };
        }

        return { data: result[0] as SwissEntity, error: null };
      } catch (err) {
        envError('Entity fetch exception', err);
        captureEntityException(err as Error, {
          operation: 'view',
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'FETCH_ERROR', message: 'Failed to fetch entity', details: err }
        };
      }
    });
  }

  async listEntities(options: DatabaseOptions = {}): Promise<DatabaseResult<SwissEntity[]>> {
    const dbCheck = requireDb<SwissEntity[]>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_search', 'list_all', async () => {
      try {
        envLog('Listing entities', { tenantId: options.tenantId });

        const result = await withDatabasePerformanceSpan('query', 'swiss_entities', async () => {
          const db = getSql();
          if (options.tenantId) {
            return await db`
              SELECT * FROM legalsyncup.swiss_entities
              WHERE tenant_id = ${options.tenantId}
              ORDER BY created_at DESC
            `;
          } else {
            return await db`
              SELECT * FROM legalsyncup.swiss_entities
              ORDER BY created_at DESC
            `;
          }
        });

        return { data: result as SwissEntity[], error: null, count: result.length };
      } catch (err) {
        envError('Entity list exception', err);
        captureEntityException(err as Error, {
          operation: 'view',
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'LIST_ERROR', message: 'Failed to list entities', details: err }
        };
      }
    });
  }

  async updateEntity(id: string, updates: SwissEntityUpdate, options: DatabaseOptions = {}): Promise<DatabaseResult<SwissEntity>> {
    const dbCheck = requireDb<SwissEntity>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_update', updates.legal_form || 'unknown', async () => {
      try {
        envLog('Updating entity', { id, tenantId: options.tenantId });

        // Build dynamic update query
        const setClauses: string[] = [];
        const values: unknown[] = [];
        let paramIndex = 1;

      if (updates.name !== undefined) {
        setClauses.push(`name = $${paramIndex++}`);
        values.push(updates.name);
      }
      if (updates.uid !== undefined) {
        setClauses.push(`uid = $${paramIndex++}`);
        values.push(updates.uid);
      }
      if (updates.legal_form !== undefined) {
        setClauses.push(`legal_form = $${paramIndex++}`);
        values.push(updates.legal_form);
      }
      if (updates.purpose !== undefined) {
        setClauses.push(`purpose = $${paramIndex++}`);
        values.push(updates.purpose);
      }
      if (updates.capital !== undefined) {
        setClauses.push(`capital = $${paramIndex++}`);
        values.push(updates.capital);
      }
      if (updates.status !== undefined) {
        setClauses.push(`status = $${paramIndex++}`);
        values.push(updates.status);
      }
      if (updates.canton !== undefined) {
        setClauses.push(`canton = $${paramIndex++}`);
        values.push(updates.canton);
      }
      if (updates.municipality !== undefined) {
        setClauses.push(`municipality = $${paramIndex++}`);
        values.push(updates.municipality);
      }

      if (setClauses.length === 0) {
        return { data: null, error: { code: 'NO_UPDATES', message: 'No fields to update' } };
      }

      // Add id to values
      values.push(id);
      const idParam = paramIndex++;

      // Add tenant_id filter if provided
      let tenantFilter = '';
      if (options.tenantId) {
        values.push(options.tenantId);
        tenantFilter = ` AND tenant_id = $${paramIndex}`;
      }

      const queryText = `
        UPDATE legalsyncup.swiss_entities
        SET ${setClauses.join(', ')}, updated_at = NOW()
        WHERE id = $${idParam}${tenantFilter}
        RETURNING *
      `;

      const result = await query<SwissEntity>(queryText, values);

      if (result.length === 0) {
        return { data: null, error: { code: 'NOT_FOUND', message: 'Entity not found' } };
      }

      // Log audit trail if not skipped
      if (!options.skipAuditLog) {
        await this.logAuditEvent('entity_updated', id, options.userId);
      }

      return { data: result[0], error: null };
      } catch (err) {
        envError('Entity update exception', err);
        captureEntityException(err as Error, {
          operation: 'update',
          legal_form: updates.legal_form ?? undefined,
          canton: updates.canton ?? undefined,
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'UPDATE_ERROR', message: 'Failed to update entity', details: err }
        };
      }
    });
  }

  async deleteEntity(id: string, options: DatabaseOptions = {}): Promise<DatabaseResult<boolean>> {
    const dbCheck = requireDb<boolean>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_update', 'delete', async () => {
      try {
        envLog('Deleting entity', { id, tenantId: options.tenantId });

        const result = await withDatabasePerformanceSpan('query', 'swiss_entities', async () => {
          const db = getSql();
          if (options.tenantId) {
            return await db`
              DELETE FROM legalsyncup.swiss_entities
              WHERE id = ${id} AND tenant_id = ${options.tenantId}
              RETURNING id
            `;
          } else {
            return await db`
              DELETE FROM legalsyncup.swiss_entities
              WHERE id = ${id}
              RETURNING id
            `;
          }
        });

        if (result.length === 0) {
          return { data: null, error: { code: 'NOT_FOUND', message: 'Entity not found' } };
        }

        // Log audit trail if not skipped
        if (!options.skipAuditLog) {
          await this.logAuditEvent('entity_deleted', id, options.userId);
        }

        return { data: true, error: null };
      } catch (err) {
        envError('Entity delete exception', err);
        captureEntityException(err as Error, {
          operation: 'delete',
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'DELETE_ERROR', message: 'Failed to delete entity', details: err }
        };
      }
    });
  }

  private async logAuditEvent(action: string, entityId: string, userId?: string): Promise<void> {
    try {
      const db = getSql();
      await db`
        INSERT INTO legalsyncup.sync_events
        (type, source, payload, processed)
        VALUES (
          ${action},
          'database',
          ${JSON.stringify({ table_name: 'swiss_entities', record_id: entityId, user_id: userId })},
          false
        )
      `;
    } catch (err) {
      envError('Failed to log audit event', err);
      // Don't throw here as audit logging is non-critical
    }
  }
}

/**
 * Document Operations Class
 * Handles legal document lifecycle management
 */
class DocumentOperations {
  async createDocument(document: ContentDocumentInsert, options: DatabaseOptions = {}): Promise<DatabaseResult<ContentDocument>> {
    const dbCheck = requireDb<ContentDocument>();
    if (dbCheck) return dbCheck;

    return withApprovalPerformanceSpan('document_validate', document.type || 'unknown', async () => {
      try {
        envLog('Creating document', { title: document.title, tenantId: options.tenantId });

        const result = await withDatabasePerformanceSpan('query', 'content_documents', async () => {
          const db = getSql();
          return await db`
            INSERT INTO legalsyncup.content_documents
            (project_id, template_id, title, type, content, language, version, approval_status, metadata, status)
            VALUES (
              ${document.project_id},
              ${document.template_id || null},
              ${document.title},
              ${document.type},
              ${document.content},
              ${document.language},
              ${document.version},
              ${document.approval_status},
              ${JSON.stringify(document.metadata || {})},
              ${document.status || 'active'}
            )
            RETURNING *
          `;
        });

        return { data: result[0] as ContentDocument, error: null };
      } catch (err) {
        envError('Document creation exception', err);
        captureApprovalWorkflowException(err as Error, {
          operation: 'start',
          document_type: document.type,
          tenant_id: options.tenantId,
        });
        return {
          data: null,
          error: { code: 'CREATE_ERROR', message: 'Failed to create document', details: err }
        };
      }
    });
  }

  async getDocumentsForProject(projectId: string, _options: DatabaseOptions = {}): Promise<DatabaseResult<ContentDocument[]>> {
    const dbCheck = requireDb<ContentDocument[]>();
    if (dbCheck) return dbCheck;

    return withApprovalPerformanceSpan('document_validate', 'get_by_project', async () => {
      try {
        envLog('Fetching documents for project', { projectId });

        const result = await withDatabasePerformanceSpan('query', 'content_documents', async () => {
          const db = getSql();
          return await db`
            SELECT * FROM legalsyncup.content_documents
            WHERE project_id = ${projectId}
            ORDER BY created_at DESC
          `;
        });

        return { data: result as ContentDocument[], error: null, count: result.length };
      } catch (err) {
        envError('Project documents fetch exception', err);
        captureApprovalWorkflowException(err as Error, {
          operation: 'start',
          document_type: 'project_documents',
        });
        return {
          data: null,
          error: { code: 'FETCH_ERROR', message: 'Failed to fetch project documents', details: err }
        };
      }
    });
  }

  async listDocuments(_options: DatabaseOptions = {}): Promise<DatabaseResult<ContentDocument[]>> {
    const dbCheck = requireDb<ContentDocument[]>();
    if (dbCheck) return dbCheck;

    return withApprovalPerformanceSpan('document_validate', 'list_all', async () => {
      try {
        envLog('Listing all documents');

        const result = await withDatabasePerformanceSpan('query', 'content_documents', async () => {
          const db = getSql();
          return await db`
            SELECT * FROM legalsyncup.content_documents
            ORDER BY created_at DESC
          `;
        });

        return { data: result as ContentDocument[], error: null, count: result.length };
      } catch (err) {
        envError('Document list exception', err);
        captureApprovalWorkflowException(err as Error, {
          operation: 'start',
          document_type: 'list_all',
        });
        return {
          data: null,
          error: { code: 'LIST_ERROR', message: 'Failed to list documents', details: err }
        };
      }
    });
  }
}

/**
 * Brand Operations Class
 * Handles brand management within entities
 */
class BrandOperations {
  async createBrand(brand: BrandInsert, _options: DatabaseOptions = {}): Promise<DatabaseResult<Brand>> {
    const dbCheck = requireDb<Brand>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_create', 'brand', async () => {
      try {
        envLog('Creating brand', { name: brand.name, entityId: brand.entity_id });

        const result = await withDatabasePerformanceSpan('query', 'brands', async () => {
          const db = getSql();
          return await db`
            INSERT INTO legalsyncup.brands
            (entity_id, name, description, logo_url, website_url, status)
            VALUES (
              ${brand.entity_id},
              ${brand.name},
              ${brand.description || null},
              ${brand.logo_url || null},
              ${brand.website_url || null},
              ${brand.status || 'active'}
            )
            RETURNING *
          `;
        });

        return { data: result[0] as Brand, error: null };
      } catch (err) {
        envError('Brand creation exception', err);
        captureEntityException(err as Error, {
          operation: 'create',
          legal_form: 'brand',
          tenant_id: _options.tenantId,
        });
        return {
          data: null,
          error: { code: 'CREATE_ERROR', message: 'Failed to create brand', details: err }
        };
      }
    });
  }

  async getBrandsForEntity(entityId: string, _options: DatabaseOptions = {}): Promise<DatabaseResult<Brand[]>> {
    const dbCheck = requireDb<Brand[]>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_search', 'brands', async () => {
      try {
        envLog('Fetching brands for entity', { entityId });

        const result = await withDatabasePerformanceSpan('query', 'brands', async () => {
          const db = getSql();
          return await db`
            SELECT * FROM legalsyncup.brands
            WHERE entity_id = ${entityId}
            ORDER BY created_at DESC
          `;
        });

        return { data: result as Brand[], error: null, count: result.length };
      } catch (err) {
        envError('Entity brands fetch exception', err);
        captureEntityException(err as Error, {
          operation: 'view',
          legal_form: 'brand',
          tenant_id: _options.tenantId,
        });
        return {
          data: null,
          error: { code: 'FETCH_ERROR', message: 'Failed to fetch entity brands', details: err }
        };
      }
    });
  }

  async listBrands(_options: DatabaseOptions = {}): Promise<DatabaseResult<Brand[]>> {
    const dbCheck = requireDb<Brand[]>();
    if (dbCheck) return dbCheck;

    return withEntityPerformanceSpan('entity_search', 'list_brands', async () => {
      try {
        envLog('Listing all brands');

        const result = await withDatabasePerformanceSpan('query', 'brands', async () => {
          const db = getSql();
          return await db`
            SELECT * FROM legalsyncup.brands
            ORDER BY created_at DESC
          `;
        });

        return { data: result as Brand[], error: null, count: result.length };
      } catch (err) {
        envError('Brand list exception', err);
        captureEntityException(err as Error, {
          operation: 'view',
          legal_form: 'brand',
          tenant_id: _options.tenantId,
        });
        return {
          data: null,
          error: { code: 'LIST_ERROR', message: 'Failed to list brands', details: err }
        };
      }
    });
  }
}

/**
 * Unified Database Operations Interface
 * Main export for the application
 */
export class Database {
  public entities: EntityOperations;
  public documents: DocumentOperations;
  public brands: BrandOperations;

  constructor() {
    this.entities = new EntityOperations();
    this.documents = new DocumentOperations();
    this.brands = new BrandOperations();
  }

  /**
   * Execute a database transaction
   * @param callback - Function to execute within the transaction
   */
  async transaction<T>(
    callback: (client: Parameters<typeof neonTransaction>[0] extends (cb: infer C extends (...args: any) => any) => unknown ? Parameters<C>[0] : never) => Promise<T>
  ): Promise<DatabaseResult<T>> {
    try {
      envLog('Starting database transaction');

      const result = await neonTransaction(callback);

      envLog('Database transaction completed successfully');
      return { data: result, error: null };
    } catch (err) {
      envError('Database transaction exception', err);
      return {
        data: null,
        error: { code: 'TRANSACTION_ERROR', message: 'Transaction failed', details: err }
      };
    }
  }

  /**
   * Health check for database connectivity
   */
  async healthCheck(): Promise<DatabaseResult<boolean>> {
    try {
      const db = getSql();
      await db`SELECT 1 as health`;
      return { data: true, error: null };
    } catch (err) {
      return {
        data: null,
        error: { code: 'HEALTH_CHECK_ERROR', message: 'Database health check failed', details: err }
      };
    }
  }
}

// Export singleton instance
export const database = new Database();

// Types DatabaseResult, DatabaseError, DatabaseOptions are defined and exported above
